/**
 * Affiliation claims — a person saying they belong to an organisation, and the organisation answering.
 */
import type {
  AffiliationClaimsResponse,
  AnswerAffiliationClaimRequest,
  ClaimAffiliationRequest,
  PublishAffiliationRequest,
  PublishedAffiliationsResponse,
} from "@ondo/shared";

import { request, requestWith } from "./request.ts";

/** Claims addressed to the caller, scoped the same way the inbox is. */
export const fetchAffiliationClaims = (principal?: string | null) =>
  request<AffiliationClaimsResponse>(
    principal
      ? `/affiliation-claims?principal=${encodeURIComponent(principal)}`
      : "/affiliation-claims",
  ).then((body) => body.claims);

/** The other side of the same rows — what this account's parties have claimed. */
export const fetchAffiliationClaimsMade = (principal?: string | null) =>
  request<AffiliationClaimsResponse>(
    principal
      ? `/affiliation-claims/made?principal=${encodeURIComponent(principal)}`
      : "/affiliation-claims/made",
  ).then((body) => body.claims);

/**
 * What a party has published, as this account is permitted to see it.
 *
 * ALWAYS RESOLVES, AND AN EMPTY LIST MEANS NOTHING IN PARTICULAR. An id that
 * does not exist, a party with nothing published, and a party whose
 * relationships are restricted away from this account are one answer — so a
 * caller must not draw "no such party" from an empty result, and no screen may
 * offer a message that says so. That indistinguishability is the whole of why
 * this read is safe to expose; see the route's docblock in `@ondo/shared`.
 */
export const fetchPublishedAffiliations = (principal: string) =>
  request<PublishedAffiliationsResponse>(
    `/affiliation-claims/published?principal=${encodeURIComponent(principal)}`,
  ).then((body) => body.affiliations);

/**
 * Who sees one of this party's relationships.
 *
 * PUT, because sending the same audience twice leaves the same audience in
 * force. Send `normaliseAudiences(...)` from `@ondo/shared`: the server widens
 * a half-stated combination rather than refusing it, and normalising first is
 * what keeps the stored row saying what the screen says.
 */
export const publishAffiliation = (id: string, body: PublishAffiliationRequest) =>
  requestWith<void>("PUT", `/affiliation-claims/${id}/publication`, body);

export const claimAffiliation = (body: ClaimAffiliationRequest) =>
  request<void>("/affiliation-claims", body);

export const answerAffiliationClaim = (id: string, body: AnswerAffiliationClaimRequest) =>
  request<void>(`/affiliation-claims/${id}/answer`, body);

export const withdrawAffiliationClaim = (id: string) =>
  request<void>(`/affiliation-claims/${id}/withdraw`, {});
